import { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Input } from '@klaro7/ui';
import { COUNTRIES } from '@klaro7/validators';

import { extractErrorMessage } from '@/lib/api';
import { usePinGate } from '@/components/PinGate';
import {
  useCancelSmsRelayMessage,
  useCreateSmsRelayDevice,
  useDeleteSmsRelayDevice,
  useRetrySmsRelayMessage,
  useRotateSmsRelayKey,
  useSendTestSms,
  useSmsRelayConfig,
  useSmsRelayDevices,
  useSmsRelayMessages,
  useSmsRelayIntlStats,
  useSmsRelayStats,
  useUpdateSmsRelayConfig,
  useUpdateSmsRelayDevice,
} from '@/features/sms-relay/hooks/useSmsRelay';
import type {
  SmsMessageStatus,
  SmsPurpose,
  SmsRelayMode,
} from '@/features/sms-relay/services/sms-relay.service';

const MODE_OPTIONS: Array<{ value: SmsRelayMode; label: string; hint: string }> = [
  { value: 'ovh', label: 'Solo OVH', hint: 'Come sempre: il provider manda tutto, la coda resta ferma.' },
  {
    value: 'relay+ovh',
    label: 'Telefono con rete di sicurezza',
    hint: 'Manda il telefono; se non risponde in tempo, il messaggio passa a OVH.',
  },
  { value: 'relay', label: 'Solo telefono', hint: 'Nessun fallback: se il telefono è giù, l\'SMS resta in coda.' },
];

const STATUS_LABELS: Record<SmsMessageStatus, string> = {
  queued: 'In coda',
  claimed: 'Preso in carico',
  sent: 'Inviato',
  delivered: 'Consegnato',
  failed: 'Fallito',
  expired: 'Scaduto',
  canceled: 'Annullato',
};

const STATUS_VARIANTS: Record<SmsMessageStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  queued: 'info',
  claimed: 'info',
  sent: 'success',
  delivered: 'success',
  failed: 'error',
  expired: 'warning',
  canceled: 'default',
};

const PURPOSE_LABELS: Record<SmsPurpose, string> = {
  otp: 'Codice OTP',
  call: 'Chiamata',
  notification: 'Notifica',
  marketing: 'Marketing',
  test: 'Prova',
};

function formatDateTime(value?: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('it-IT');
}

/** Bandiera del prefisso, quando è riconoscibile: rende la tabella leggibile a colpo d'occhio. */
function flagForDial(dial: string): string {
  return COUNTRIES.find((c) => c.dial === dial)?.flag ?? '🌐';
}

function formatAgo(value?: string): string {
  if (!value) return 'mai';
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s fa`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min fa`;
  return `${Math.round(seconds / 3600)} h fa`;
}

/** Soglia lato server (settings.smsRelay.alertBatteryPercent); 0 = allerta spenta. */
function isBatteryLow(battery: number, threshold: number | undefined): boolean {
  if (!threshold || threshold <= 0) return false;
  return battery < threshold;
}

export function AdminSmsRelayPage() {
  const { data: config } = useSmsRelayConfig();
  const { data: devices, isLoading: devicesLoading } = useSmsRelayDevices();
  const { data: stats } = useSmsRelayStats();
  const { data: intlStats } = useSmsRelayIntlStats();

  const [filters, setFilters] = useState<{ page: number; status: SmsMessageStatus | ''; purpose: SmsPurpose | '' }>({
    page: 1,
    status: '',
    purpose: '',
  });
  const { data: messages } = useSmsRelayMessages(filters);

  const updateConfig = useUpdateSmsRelayConfig();
  const createDevice = useCreateSmsRelayDevice();
  const updateDevice = useUpdateSmsRelayDevice();
  const rotateKey = useRotateSmsRelayKey();
  const deleteDevice = useDeleteSmsRelayDevice();
  const retryMessage = useRetrySmsRelayMessage();
  const cancelMessage = useCancelSmsRelayMessage();
  const sendTest = useSendTestSms();
  const { requirePin, pinDialog } = usePinGate();

  const [newDevice, setNewDevice] = useState({ name: '', simNumber: '' });
  // La chiave in chiaro esiste solo in questo stato: alla chiusura sparisce.
  const [issuedKey, setIssuedKey] = useState<{ name: string; apiKey: string } | null>(null);
  const [testTo, setTestTo] = useState('');

  async function handleMode(mode: SmsRelayMode) {
    try {
      await updateConfig.mutateAsync({ mode });
      toast.success('Modalità aggiornata');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleCreateDevice() {
    if (!newDevice.name.trim()) {
      toast.error('Dai un nome al telefono');
      return;
    }
    try {
      const result = await createDevice.mutateAsync({
        name: newDevice.name.trim(),
        simNumber: newDevice.simNumber.trim() || undefined,
      });
      setIssuedKey({ name: result.device.name, apiKey: result.apiKey });
      setNewDevice({ name: '', simNumber: '' });
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleRotate(id: string, name: string) {
    try {
      const result = await rotateKey.mutateAsync(id);
      setIssuedKey({ name, apiKey: result.apiKey });
      toast.success('Chiave rigenerata: quella vecchia non funziona più');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(`Eliminare il device "${name}"? La sua chiave smetterà di funzionare.`)) return;
    try {
      await deleteDevice.mutateAsync(id);
      toast.success('Device eliminato');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleTest() {
    try {
      await sendTest.mutateAsync({ to: testTo.trim() });
      toast.success('SMS di prova accodato');
      setTestTo('');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  const counts = stats?.counts ?? {};
  const queued = counts.queued ?? 0;
  const noDeviceOnline = (devices ?? []).every((d) => !d.online);
  const relayActive = config?.mode !== 'ovh';

  return (
    <div className="space-y-6">
      {pinDialog}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Gateway SMS</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Invio SMS tramite un telefono Android con SIM, al posto del provider OVH. Il telefono preleva i messaggi
          dalla coda e conferma l'invio.
        </p>
      </div>

      {relayActive && queued > 0 && noDeviceOnline && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Ci sono {queued} messaggi in coda e nessun telefono collegato.{' '}
          {config?.mode === 'relay+ovh'
            ? 'Passeranno a OVH allo scadere del fallback.'
            : 'In modalità "solo telefono" resteranno fermi finché il device non torna online.'}
        </div>
      )}

      {/* ---- Modalità ---- */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <h3 className="text-sm font-semibold text-neutral-700">Modalità di invio</h3>
          <p className="text-xs text-neutral-600">Il cambio è immediato e reversibile in qualsiasi momento.</p>
        </div>
        <div className="grid gap-3 p-5 sm:grid-cols-3">
          {MODE_OPTIONS.map((option) => {
            const active = config?.mode === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => requirePin(() => handleMode(option.value))}
                disabled={updateConfig.isPending}
                className={`rounded-lg border p-4 text-left transition-colors ${
                  active
                    ? 'border-brand-teal bg-brand-teal/5 ring-1 ring-brand-teal'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <span className="block text-sm font-semibold text-neutral-900">{option.label}</span>
                <span className="mt-1 block text-xs text-neutral-600">{option.hint}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- Coda ---- */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <h3 className="text-sm font-semibold text-neutral-700">Coda</h3>
        </div>
        <div className="flex flex-wrap gap-6 p-5">
          {(['queued', 'claimed', 'sent', 'failed', 'expired'] as SmsMessageStatus[]).map((status) => (
            <div key={status}>
              <div className="text-2xl font-bold text-neutral-900">{counts[status] ?? 0}</div>
              <div className="text-xs text-neutral-600">{STATUS_LABELS[status]}</div>
            </div>
          ))}
          <div>
            <div className="text-2xl font-bold text-neutral-900">{formatAgo(stats?.oldestQueuedAt)}</div>
            <div className="text-xs text-neutral-600">Messaggio più vecchio in coda</div>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 border-t border-neutral-100 p-5">
          <div className="w-full sm:w-64">
            <label className="mb-1 block text-xs font-medium text-neutral-700">SMS di prova</label>
            <Input placeholder="+39333..." value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          </div>
          <Button variant="secondary" onClick={() => void handleTest()} loading={sendTest.isPending}>
            Invia prova
          </Button>
        </div>
      </div>

      {/* ---- SMS internazionali ---- */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-baseline justify-between gap-3 border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">SMS internazionali</h3>
            <p className="text-xs text-neutral-600">
              La SIM del telefono ha SMS illimitati solo verso l&apos;Italia: qui si vede quanto costa il resto.
            </p>
          </div>
          <span className="shrink-0 text-xs text-neutral-500">{intlStats?.month}</span>
        </div>

        {!intlStats || intlStats.items.length === 0 ? (
          <p className="p-5 text-sm text-neutral-600">Nessun SMS verso l&apos;estero questo mese.</p>
        ) : (
          <div className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-600">
                    <th className="pb-2 font-medium">Paese</th>
                    <th className="pb-2 text-right font-medium">Inviati</th>
                    <th className="pb-2 text-right font-medium">Falliti</th>
                  </tr>
                </thead>
                <tbody>
                  {intlStats.items.map((row) => (
                    <tr key={row.dial} className="border-t border-neutral-100">
                      <td className="py-2 text-neutral-900">
                        <span className="mr-2">{flagForDial(row.dial)}</span>
                        {row.dial}
                      </td>
                      <td className="py-2 text-right text-neutral-900">{row.sent}</td>
                      <td className="py-2 text-right text-neutral-900">{row.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-neutral-600">
              Totale {intlStats.total.sent} inviati, {intlStats.total.failed} falliti. Oltre{' '}
              {intlStats.threshold} invii nel mese scatta un&apos;allerta nel Log Errori: è un avviso, non
              un blocco.
            </p>
          </div>
        )}
      </div>

      {/* ---- Device ---- */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <h3 className="text-sm font-semibold text-neutral-700">Telefoni collegati</h3>
          <p className="text-xs text-neutral-600">
            Ogni telefono ha la sua chiave. Metterlo in pausa lo lascia collegato ma fermo.
          </p>
        </div>

        {issuedKey && (
          <div className="border-b border-neutral-100 bg-emerald-50 p-5">
            <p className="text-sm font-semibold text-emerald-900">
              Chiave per «{issuedKey.name}» — visibile solo adesso
            </p>
            <p className="mt-1 text-xs text-emerald-800">
              Copiala nell'app Android. Non sarà più recuperabile: in caso di smarrimento va rigenerata.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <code className="flex-1 break-all rounded border border-emerald-200 bg-white px-3 py-2 text-xs text-neutral-800">
                {issuedKey.apiKey}
              </code>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard.writeText(issuedKey.apiKey);
                  toast.success('Chiave copiata');
                }}
              >
                Copia
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIssuedKey(null)}>
                Ho finito
              </Button>
            </div>
          </div>
        )}

        <div className="divide-y divide-neutral-100">
          {devicesLoading && <div className="p-5 text-sm text-neutral-500">Caricamento…</div>}
          {!devicesLoading && (devices ?? []).length === 0 && (
            <div className="p-5 text-sm text-neutral-500">Nessun telefono registrato.</div>
          )}
          {(devices ?? []).map((device) => (
            <div key={device.id} className="flex flex-col gap-3 p-5 lg:flex-row lg:items-center">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-neutral-900">{device.name}</span>
                  <Badge variant={device.online ? 'success' : 'error'}>
                    {device.online ? 'Online' : 'Offline'}
                  </Badge>
                  {device.paused && <Badge variant="warning">In pausa</Badge>}
                  {!device.active && <Badge variant="default">Revocato</Badge>}
                </div>
                <div className="mt-1 text-xs text-neutral-600">
                  Chiave {device.keyPrefix}… · {device.simNumber || 'SIM non indicata'} · visto {formatAgo(device.lastSeenAt)} ·{' '}
                  {device.sentCount} inviati / {device.failedCount} falliti · max {device.maxPerHour}/h
                </div>
                {device.lastHeartbeat && (
                  <div className="mt-1 text-xs text-neutral-500">
                    <span
                      className={
                        isBatteryLow(device.lastHeartbeat.battery, config?.alertBatteryPercent)
                          ? 'font-semibold text-red-600'
                          : undefined
                      }
                    >
                      Batteria {device.lastHeartbeat.battery}%{device.lastHeartbeat.charging ? ' (in carica)' : ''}
                    </span>{' '}
                    · SIM {device.lastHeartbeat.simState} · app {device.lastHeartbeat.appVersion || '—'}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    requirePin(() => updateDevice.mutateAsync({ id: device.id, data: { paused: !device.paused } }))
                  }
                >
                  {device.paused ? 'Riattiva' : 'Pausa'}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    requirePin(() => updateDevice.mutateAsync({ id: device.id, data: { active: !device.active } }))
                  }
                >
                  {device.active ? 'Revoca' : 'Riabilita'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => requirePin(() => handleRotate(device.id, device.name))}>
                  Nuova chiave
                </Button>
                <Button size="sm" variant="ghost" onClick={() => requirePin(() => handleDelete(device.id, device.name))}>
                  Elimina
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t border-neutral-100 bg-neutral-50 p-5">
          <div className="w-full sm:w-56">
            <label className="mb-1 block text-xs font-medium text-neutral-700">Nome telefono</label>
            <Input
              placeholder="Pixel ufficio"
              value={newDevice.name}
              onChange={(e) => setNewDevice((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div className="w-full sm:w-56">
            <label className="mb-1 block text-xs font-medium text-neutral-700">Numero SIM (opzionale)</label>
            <Input
              placeholder="+39333..."
              value={newDevice.simNumber}
              onChange={(e) => setNewDevice((p) => ({ ...p, simNumber: e.target.value }))}
            />
          </div>
          <Button onClick={() => requirePin(handleCreateDevice)} loading={createDevice.isPending}>
            Registra telefono
          </Button>
        </div>
      </div>

      {/* ---- Messaggi ---- */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Messaggi</h3>
            <p className="text-xs text-neutral-600">
              I numeri sono mascherati e il testo dei codici OTP non viene mostrato.
            </p>
          </div>
          <div className="flex gap-2">
            <select
              className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-800"
              value={filters.status}
              onChange={(e) =>
                setFilters((p) => ({ ...p, page: 1, status: e.target.value as SmsMessageStatus | '' }))
              }
            >
              <option value="">Tutti gli stati</option>
              {(Object.keys(STATUS_LABELS) as SmsMessageStatus[]).map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
            <select
              className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-800"
              value={filters.purpose}
              onChange={(e) => setFilters((p) => ({ ...p, page: 1, purpose: e.target.value as SmsPurpose | '' }))}
            >
              <option value="">Tutti i tipi</option>
              {(Object.keys(PURPOSE_LABELS) as SmsPurpose[]).map((purpose) => (
                <option key={purpose} value={purpose}>
                  {PURPOSE_LABELS[purpose]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white text-left text-xs uppercase text-neutral-500">
              <tr>
                <th className="px-5 py-3 font-medium">Data</th>
                <th className="px-5 py-3 font-medium">Destinatario</th>
                <th className="px-5 py-3 font-medium">Tipo</th>
                <th className="px-5 py-3 font-medium">Stato</th>
                <th className="px-5 py-3 font-medium">Canale</th>
                <th className="px-5 py-3 font-medium">Dettagli</th>
                <th className="px-5 py-3 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {(messages?.items ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-sm text-neutral-500">
                    Nessun messaggio.
                  </td>
                </tr>
              )}
              {(messages?.items ?? []).map((message) => (
                <tr key={message.id}>
                  <td className="whitespace-nowrap px-5 py-3 text-neutral-700">
                    {formatDateTime(message.createdAt)}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-neutral-900">{message.to}</td>
                  <td className="whitespace-nowrap px-5 py-3 text-neutral-700">
                    {PURPOSE_LABELS[message.purpose]}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3">
                    <Badge variant={STATUS_VARIANTS[message.status]}>{STATUS_LABELS[message.status]}</Badge>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-neutral-700">
                    {message.sentVia === 'ovh' ? 'OVH' : message.sentVia === 'relay' ? 'Telefono' : '—'}
                  </td>
                  <td className="px-5 py-3 text-xs text-neutral-600">
                    {message.errorCode ? `${message.errorCode}: ${message.errorMessage ?? ''}` : ''}
                    {message.attempts > 1 && ` · ${message.attempts} tentativi`}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right">
                    {(message.status === 'failed' || message.status === 'expired') && (
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void retryMessage.mutateAsync({ id: message.id, via: 'relay' })}
                          title="Rimette il messaggio in coda: lo prende il telefono (o il fallback OVH se previsto)."
                        >
                          Riprova (telefono)
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void retryMessage.mutateAsync({ id: message.id, via: 'ovh' })}
                          title="Invia subito tramite OVH, senza passare dal telefono."
                        >
                          Riprova (OVH)
                        </Button>
                      </div>
                    )}
                    {(message.status === 'queued' || message.status === 'claimed') && (
                      <Button size="sm" variant="ghost" onClick={() => void cancelMessage.mutateAsync(message.id)}>
                        Annulla
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {messages && messages.total > messages.limit && (
          <div className="flex items-center justify-between border-t border-neutral-100 p-4 text-sm text-neutral-600">
            <span>
              Pagina {messages.page} di {Math.ceil(messages.total / messages.limit)} · {messages.total} messaggi
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={filters.page <= 1}
                onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))}
              >
                Precedente
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={filters.page >= Math.ceil(messages.total / messages.limit)}
                onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
              >
                Successiva
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
